import { computed } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import { differenceInCalendarDays, parseISO, format } from 'date-fns';

export const useTokensReport = () => {
  const store = useStore();

  // Auth helpers
  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  };

  const getAuthHeaders = () => {
    const cookie = getCookie('cw_d_session_info');
    if (!cookie) return {};
    try {
      const sessionInfo = JSON.parse(cookie);
      return {
        'access-token': sessionInfo['access-token'],
        client: sessionInfo['client'],
        uid: sessionInfo['uid']
      };
    } catch (e) {
      console.error('Erro ao ler o cookie:', e);
      return {};
    }
  };

  // State from store
  const bulkData = computed(() => store.getters['interactionsReport/getBulkData']);
  const isLoading = computed(() => store.getters['interactionsReport/isLoading']);
  const error = computed(() => store.getters['interactionsReport/getError']);

  // Core computed slices
  const summary = computed(() => bulkData.value?.bulk_data?.summary || {});
  const dailyBreakdown = computed(() => bulkData.value?.bulk_data?.daily_breakdown || []);
  const conversationMetrics = computed(() => bulkData.value?.bulk_data?.conversation_metrics || []);

  // Main fetch function - delegates to store
  const fetchData = async (params = {}) => {
    const headers = getAuthHeaders();
    await store.dispatch('interactionsReport/fetchBulkData', {
      since: params.since,
      until: params.until,
      headers,
    });
  };

  // Computed metrics
  const daysInRange = computed(() => {
    if (!summary.value?.date_range?.since || !summary.value?.date_range?.until_date) {
      return 1;
    }
    return differenceInCalendarDays(
      parseISO(summary.value.date_range.until_date),
      parseISO(summary.value.date_range.since)
    ) + 1;
  });

  const avgInteractionsPerDay = computed(() => {
    const total = summary.value.total_interactions || 0;
    return Math.round(total / daysInRange.value);
  });

  const avgInteractionsPerConversation = computed(() => {
    const total = summary.value.total_interactions || 0;
    const conversations = summary.value.conversations_with_interactions || 1;
    return Math.round(total / conversations);
  });

  const billableInteractions = computed(() => {
    return summary.value.billable_interactions || 0;
  });

  const tokenLimit = computed(() => {
    return (
      bulkData.value?.token_limit ??
      bulkData.value?.bulk_data?.token_limit ??
      1000
    );
  });

  const tokenPeriodStart = computed(() => bulkData.value?.token_period_start || null);
  const tokenPeriodEnd = computed(() => bulkData.value?.token_period_end || null);
  const tokenCycleAnchorDate = computed(() => bulkData.value?.token_cycle_anchor_date || null);

  // Deleted billable interactions count (for UI feedback)
  const deletedBillableInteractions = computed(() => {
    return summary.value.deleted_billable_interactions || 0;
  });
  
  // Calculate total AI audio interactions from conversation metrics
  const totalAiAudioInteractions = computed(() => {
    return conversationMetrics.value.reduce((sum, conv) => sum + (conv.total_ai_audio_interactions || 0), 0);
  });

  // Chart data
  const dailyChartData = computed(() => {
    if (!summary.value?.date_range?.since || !summary.value?.date_range?.until_date) {
      return { labels: [], datasets: [] };
    }

    const startDate = parseISO(summary.value.date_range.since);
    const endDate = parseISO(summary.value.date_range.until_date);

    const dataMap = new Map();
    dailyBreakdown.value.forEach(d => {
      dataMap.set(d.date, d);
    });
    
    // Generate all dates in range
    const allLabels = [];
    const aiData = [];
    const humanData = [];
    const deletedAiData = [];

    let currentDate = startDate;
    while (currentDate <= endDate) {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const dayData = dataMap.get(dateStr);

      allLabels.push(format(currentDate, 'dd/MM'));
      aiData.push(dayData?.total_ai_interactions || 0);
      humanData.push((dayData?.total_interactions || 0) - (dayData?.total_ai_interactions || 0));
      deletedAiData.push(dayData?.deleted_ai_interactions || 0);

      // Move to next day
      currentDate = new Date(currentDate);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    const hasDeletedData = deletedAiData.some(v => v > 0);

    const datasets = [
      {
        label: 'Interações IA',
        data: aiData,
        borderColor: 'rgba(59, 130, 246, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.3,
        pointRadius: 4,
        pointBackgroundColor: 'rgba(59, 130, 246, 1)',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2
      },
      {
        label: 'Interações Humanas',
        data: humanData,
        borderColor: 'rgba(156, 163, 175, 1)', // Lighter gray
        backgroundColor: 'rgba(156, 163, 175, 0.1)',
        tension: 0.3,
        pointRadius: 4,
        pointBackgroundColor: 'rgba(156, 163, 175, 1)',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2
      }
    ];

    if (hasDeletedData) {
      datasets.push({
        label: 'Interações IA (excluídas)',
        data: deletedAiData,
        borderColor: 'rgba(239, 68, 68, 1)', // Red-500
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        tension: 0.3,
        pointRadius: 4,
        pointBackgroundColor: 'rgba(239, 68, 68, 1)',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2
      });
    }

    return { labels: allLabels, datasets };
  });

  // Fetch comparison data for a previous period (direct HTTP, no store)
  const fetchComparisonData = async (since, until) => {
    const headers = getAuthHeaders();
    try {
      const response = await axios.get('/api/v1/interactions/bulk_data', {
        headers,
        params: { since, until, limit: 10000, offset: 0 },
      });
      const data = response.data?.bulk_data;
      const metrics = data?.conversation_metrics || [];
      return {
        billableInteractions: data?.summary?.billable_interactions || 0,
        totalAiAudioInteractions: metrics.reduce(
          (sum, conv) => sum + (conv.total_ai_audio_interactions || 0), 0
        ),
        conversationsCount: metrics.length,
      };
    } catch {
      return null;
    }
  };

  // Data availability checks
  const hasDailyData = computed(() => dailyBreakdown.value.length > 0);
  const hasConversationData = computed(() => conversationMetrics.value.length > 0);

  return {
    bulkData,
    summary,
    dailyBreakdown,
    conversationMetrics,
    isLoading,
    error,
    fetchData,
    fetchComparisonData,
    daysInRange,
    avgInteractionsPerDay,
    avgInteractionsPerConversation,
    billableInteractions,
    tokenLimit,
    tokenPeriodStart,
    tokenPeriodEnd,
    tokenCycleAnchorDate,
    deletedBillableInteractions,
    totalAiAudioInteractions,
    dailyChartData,
    hasDailyData,
    hasConversationData
  };
};
